import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { labResultService } from '../services/labResultService'
import { LabResultFlag } from '../types/labResult'
import { formatDate } from '../utils/locale'
import { taskService } from '../services/taskService'
import { TaskForm } from '../components/TaskForm'
import { Modal } from '../components/Modal'
import './LabResultDetail.css'

export function LabResultDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCreateTask, setShowCreateTask] = useState(false)

  const { data: result, isLoading, error } = useQuery({
    queryKey: ['lab-result', id],
    queryFn: () => labResultService.getById(id!),
    enabled: !!id,
  })

  const reviewMutation = useMutation({
    mutationFn: () => labResultService.markAsReviewed(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['lab-result', id] })
      queryClient.invalidateQueries({ queryKey: ['lab-results'] })
    },
  })

  const handleMarkAsReviewed = async () => {
    if (window.confirm('Mark this lab result as reviewed?')) {
      try {
        await reviewMutation.mutateAsync()
        alert('Lab result marked as reviewed')
      } catch (error) {
        alert('Failed to mark result as reviewed')
      }
    }
  }

  const getFlagBadge = (flag?: LabResultFlag) => {
    if (!flag || flag === 'normal') return null
    
    const flagClasses = {
      high: 'flag-high',
      low: 'flag-low',
      'critical-high': 'flag-critical-high',
      'critical-low': 'flag-critical-low',
      abnormal: 'flag-abnormal',
    }
    
    const flagLabels = {
      high: '↑ High',
      low: '↓ Low',
      'critical-high': '↑↑ Critical High',
      'critical-low': '↓↓ Critical Low',
      abnormal: '⚠ Abnormal',
    }
    
    return (
      <span className={`test-flag ${flagClasses[flag]}`}>
        {flagLabels[flag]}
      </span>
    )
  }

  if (isLoading) return <div className="loading">Loading lab result...</div>
  if (error) return <div className="error">Error loading lab result</div>
  if (!result) return <div>Lab result not found</div>

  return (
    <div className="lab-result-detail">
      <div className="lab-result-header">
        <div className="header-left">
          <button className="btn-back" onClick={() => navigate('/lab-results')}>
            ← Back to Lab Results
          </button>
          <div>
            <h1>Lab Result</h1>
            <div className="result-meta">
              <span className="result-order">
                {result.orderNumber && `Order: ${result.orderNumber}`}
                {result.accessionNumber && ` • Accession: ${result.accessionNumber}`}
              </span>
            </div>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={() => setShowCreateTask(true)}>
            + Create Task
          </button>
          {result.reviewStatus === 'in-review' && (
            <button
              className="btn-primary"
              onClick={handleMarkAsReviewed}
              disabled={reviewMutation.isPending}
            >
              {reviewMutation.isPending ? 'Marking...' : 'Mark as Reviewed'}
            </button>
          )}
          <button className="btn-secondary" onClick={() => navigate(`/patients/${result.patientId}`)}>
            View Patient
          </button>
        </div>
      </div>

      <div className="lab-result-content">
        <div className="result-section">
          <h2>Result Information</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Lab Name</label>
              <p>{result.labName}</p>
            </div>
            {result.labCode && (
              <div className="info-item">
                <label>Lab Code</label>
                <p>{result.labCode}</p>
              </div>
            )}
            {result.orderingPhysicianName && (
              <div className="info-item">
                <label>Ordering Physician</label>
                <p>{result.orderingPhysicianName}</p>
              </div>
            )}
            <div className="info-item">
              <label>Collection Date</label>
              <p>
                {formatDate(result.collectionDate)}
                {result.collectionTime && ` at ${result.collectionTime}`}
              </p>
            </div>
            <div className="info-item">
              <label>Reported Date</label>
              <p>
                {formatDate(result.reportedDate)}
                {result.reportedTime && ` at ${result.reportedTime}`}
              </p>
            </div>
            <div className="info-item">
              <label>Status</label>
              <p>
                <span className={`status-badge status-${result.status}`}>{result.status}</span>
              </p>
            </div>
            <div className="info-item">
              <label>Review Status</label>
              <p>
                {result.reviewStatus === 'in-review' && (
                  <span className="review-badge in-review">In Review</span>
                )}
                {result.reviewStatus === 'reviewed' && (
                  <span className="review-badge reviewed">Reviewed</span>
                )}
                {result.reviewedByName && (
                  <span className="review-info">
                    {' '}by {result.reviewedByName}
                    {result.reviewedAt && ` on ${formatDate(result.reviewedAt)}`}
                  </span>
                )}
              </p>
            </div>
            {result.clinicalIndication && (
              <div className="info-item full-width">
                <label>Clinical Indication</label>
                <p>{result.clinicalIndication}</p>
              </div>
            )}
            {result.comments && (
              <div className="info-item full-width">
                <label>Comments</label>
                <div className="comments-content">{result.comments}</div>
              </div>
            )}
            <div className="info-item">
              <label>Source</label>
              <p>
                <span className="source-badge">{result.source}</span>
              </p>
            </div>
            {result.healthlinkMessageId && (
              <div className="info-item">
                <label>Healthlink Message ID</label>
                <p>{result.healthlinkMessageId}</p>
              </div>
            )}
          </div>
        </div>

        <div className="result-section">
          <h2>Test Results</h2>
          {result.panels.map((panel) => (
            <div key={panel.id} className="test-panel">
              <div className="panel-header">
                <h3>
                  {panel.panelName} ({panel.panelCode})
                </h3>
              </div>
              <table className="test-results-table">
                <thead>
                  <tr>
                    <th>Test Name</th>
                    <th>Value</th>
                    <th>Reference Range</th>
                    <th>Flag</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {panel.tests.map((test) => (
                    <tr
                      key={test.id}
                      className={
                        test.flag === 'critical-high' || test.flag === 'critical-low'
                          ? 'critical-test'
                          : test.flag === 'high' || test.flag === 'low'
                          ? 'abnormal-test'
                          : ''
                      }
                    >
                      <td>
                        <div className="test-name">
                          <strong>{test.testName}</strong>
                          {test.testCode && <span className="test-code">({test.testCode})</span>}
                        </div>
                      </td>
                      <td>
                        <span className="test-value">
                          {test.value} {test.unit && <span className="test-unit">{test.unit}</span>}
                        </span>
                      </td>
                      <td className="reference-range">{test.referenceRange || '-'}</td>
                      <td>{getFlagBadge(test.flag)}</td>
                      <td>
                        <span className={`status-badge status-${test.status}`}>{test.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {panel.tests.some((t) => t.notes) && (
                <div className="panel-notes">
                  {panel.tests
                    .filter((t) => t.notes)
                    .map((test) => (
                      <div key={test.id} className="test-note">
                        <strong>{test.testName}:</strong> {test.notes}
                      </div>
                    ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <Modal
        isOpen={showCreateTask}
        onClose={() => setShowCreateTask(false)}
        title="Create Task"
        size="medium"
      >
        <TaskForm
          patientId={result.patientId}
          ehrComponentType="lab-result"
          ehrComponentId={result.id}
          onSubmit={async (data) => {
            await taskService.createForPatient(result.patientId, data)
            queryClient.invalidateQueries({ queryKey: ['tasks'] })
            setShowCreateTask(false)
          }}
          onCancel={() => setShowCreateTask(false)}
        />
      </Modal>
    </div>
  )
}

